'use client';
import './styles.scss';

import { Input } from '@heroui/react';
import { useAction } from '@reatom/npm-react';
import { Path } from '@trwl/shared/lib/router';
import { Search as SearchIcon } from '@trwl/shared/ui/icons';
import { addRecentSearchAction, SEARCH } from '@trwl/store';
import { serializeFilterToQuery } from '@trwl/store/filter/serializer';
import { ym } from '@trwl/util';
import { useUnit } from 'effector-react';
import { useRouter } from 'next/navigation';
import { FC, FocusEventHandler, useCallback, useEffect, useState } from 'react';

interface SearchProps {
  onClose?: () => void;
  autofocus?: boolean;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
}

const phrases = ['Poizon', 'Dewu'];
const dewuLinkMatcher = /https:\/\/dw4.co\/t\/A\/\w+/g;

export const Search: FC<SearchProps> = ({
  onClose,
  autofocus,
  onFocus,
  onBlur,
}) => {
  const addRecentSearch = useAction(addRecentSearchAction);
  const [searchQuery, setSearchQuery] = useUnit([
    SEARCH.$search,
    SEARCH.setSearch,
  ]);
  const [currentPhrase, setCurrentPhrase] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [speed, setSpeed] = useState(150);

  const router = useRouter();

  useEffect(() => {
    const handleTyping = () => {
      if (searchQuery?.length) return;
      const currentWord = phrases[loopNum % phrases.length];

      if (isDeleting) {
        setCurrentPhrase(currentWord.substring(0, charIndex - 1));
        setCharIndex((prev) => prev - 1);
        setSpeed(50);
      } else {
        setCurrentPhrase(currentWord.substring(0, charIndex + 1));
        setCharIndex((prev) => prev + 1);
        setSpeed(150);
      }

      if (!isDeleting && charIndex === currentWord.length) {
        setTimeout(() => setIsDeleting(true), 1000);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setLoopNum((prev) => prev + 1);
      }
    };

    const timer = setTimeout(handleTyping, speed);

    return () => clearTimeout(timer);
  }, [searchQuery, charIndex, isDeleting, loopNum, speed]);

  const handleSearch = useCallback(
    (query: string | undefined) => {
      const trimmedQuery = query?.trim();
      if (trimmedQuery) addRecentSearch(trimmedQuery);
      if (onClose) onClose();
      setSearchQuery('');
      ym('search-product');
      router.push(
        `${Path.CATALOG}?filter=${serializeFilterToQuery({
          keyword: trimmedQuery,
        })}`,
      );
    },
    [addRecentSearch, onClose, router, setSearchQuery],
  );

  const prepareTitle = useCallback((title: string) => {
    const match = title?.match(dewuLinkMatcher);
    if (match) {
      return match[0];
    }
    return title;
  }, []);

  return (
    <form action="." className={'contents'}>
      <Input
        isClearable
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autofocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Return') {
            e.preventDefault();
            handleSearch(searchQuery);
          }
        }}
        name={'search'}
        value={searchQuery}
        onValueChange={(value) => {
          setSearchQuery(prepareTitle(value));
        }}
        onClear={() => {
          setSearchQuery('');
        }}
        classNames={{
          input: 'text-[14px] text-medium',
          inputWrapper: 'rounded-[14px]',
        }}
        startContent={
          <SearchIcon
            onClick={() => {
              handleSearch(searchQuery);
            }}
            className="h-[22px] min-w-[22px] cursor-pointer"
          />
        }
        aria-labelledby="unique-id"
        placeholder={'Ссылка или название с ' + currentPhrase}
        type="search"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        maxLength={100}
        spellCheck="false"
      />
    </form>
  );
};
